import { create } from "zustand";
import type {
  PrFlowDraftingFailureReason,
  PrFlowDraftingFieldSource,
  PrFlowDraftingMetadata,
  PrEntitySnapshot,
  PrEntityState,
  PrFlowOperationKind,
  PrFlowOperationRecord,
  PrFlowOperationRequestPayload,
  PrFlowOperationResultPayload,
  PrFlowOperationStatus,
  PrFlowOperationStep,
  RepoReadinessSnapshot,
  SessionPrFlowSnapshot,
} from "@cord/shared";

type SnapshotEntry = SessionPrFlowSnapshot | null;

const PR_FLOW_RENDERABLE_KINDS = new Set<PrFlowOperationKind>([
  "quick_pr",
  "update_pr",
  "manual_create_pr",
])

const PR_FLOW_TERMINAL_SUCCESS_STATUSES = new Set<PrFlowOperationStatus>([
  "succeeded",
  "reconciled",
])

interface SessionPrFlowStore {
  snapshots: Record<string, SnapshotEntry>;
  snapshotTimestamps: Record<string, number | null>;
  setSnapshot: (sessionId: string, snapshot: SnapshotEntry) => boolean;
  clearSnapshot: (sessionId: string) => void;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeNumber(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.floor(value);
}

function normalizeBoolean(value: unknown): boolean | null {
  if (typeof value !== "boolean") return null;
  return value;
}

function normalizeDraftingFieldSource(value: unknown): PrFlowDraftingFieldSource | null {
  return value === "provided" ||
    value === "ai_generated" ||
    value === "thread_title" ||
    value === "commit_subject" ||
    value === "changed_files" ||
    value === "branch_name" ||
    value === "default"
    ? value
    : null;
}

function normalizeDraftingFailureReason(value: unknown): PrFlowDraftingFailureReason | null {
  return value === "no_context" ||
    value === "no_model" ||
    value === "model_timeout" ||
    value === "model_error" ||
    value === "empty_response" ||
    value === "invalid_response"
    ? value
    : null;
}

function normalizeDraftingMetadata(value: unknown): PrFlowDraftingMetadata | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const commitMessageSource = normalizeDraftingFieldSource(row.commitMessageSource);
  const prTitleSource = normalizeDraftingFieldSource(row.prTitleSource);
  const prBodySource = normalizeDraftingFieldSource(row.prBodySource);
  if (!commitMessageSource || !prTitleSource || !prBodySource) return null;

  return {
    commitMessageSource,
    prTitleSource,
    prBodySource,
    aiAttempted: row.aiAttempted === true,
    aiFailureReason: normalizeDraftingFailureReason(row.aiFailureReason),
    aiFailureMessage: normalizeString(row.aiFailureMessage),
    context:
      row.context && typeof row.context === "object" && !Array.isArray(row.context)
        ? (row.context as PrFlowDraftingMetadata["context"])
        : null,
  };
}

function normalizePrEntityState(value: unknown): PrEntityState {
  return value === "draft" || value === "open" || value === "merged" || value === "closed"
    ? value
    : "none";
}

function normalizeOperationKind(value: unknown): PrFlowOperationKind | null {
  return value === "manual_create_pr" ||
    value === "quick_pr" ||
    value === "update_pr" ||
    value === "merge_pr" ||
    value === "archive_thread" ||
    value === "yolo"
    ? value
    : null;
}

function normalizeOperationStatus(value: unknown): PrFlowOperationStatus | null {
  return value === "accepted" ||
    value === "running" ||
    value === "succeeded" ||
    value === "failed" ||
    value === "cancelled" ||
    value === "reconciled"
    ? value
    : null;
}

function normalizeOperationStep(value: unknown): PrFlowOperationStep {
  return value === "validating" ||
    value === "queued" ||
    value === "resuming_sandbox" ||
    value === "awaiting_draft" ||
    value === "awaiting_finalize" ||
    value === "staging" ||
    value === "committing" ||
    value === "pushing" ||
    value === "creating_pr" ||
    value === "linking_existing_pr" ||
    value === "success" ||
    value === "failure"
    ? value
    : "idle";
}

function normalizeOperationPayload(
  value: unknown,
): PrFlowOperationRequestPayload | PrFlowOperationResultPayload | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  return {
    commitMessage: normalizeString(row.commitMessage),
    prTitle: normalizeString(row.prTitle),
    prBody: normalizeString(row.prBody),
    draft: normalizeBoolean(row.draft),
    source:
      row.source === "fallback" || row.source === "command" || row.source === "manual"
        ? row.source
        : null,
    drafting: normalizeDraftingMetadata(row.drafting),
    conversationId: normalizeString(row.conversationId),
    prNumber: normalizeNumber(row.prNumber),
    prUrl: normalizeString(row.prUrl),
    prState:
      row.prState === "draft" || row.prState === "open" || row.prState === "merged" || row.prState === "closed"
        ? row.prState
        : null,
    updatedExisting: normalizeBoolean(row.updatedExisting),
    reconciledFromWebhook: normalizeBoolean(row.reconciledFromWebhook),
  } as PrFlowOperationRequestPayload & PrFlowOperationResultPayload;
}

function normalizeOperation(value: unknown): PrFlowOperationRecord | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const operationId = normalizeString(row.operationId);
  const kind = normalizeOperationKind(row.kind);
  const status = normalizeOperationStatus(row.status);
  if (!operationId || !kind || !status) return null;

  return {
    operationId,
    kind,
    status,
    step: normalizeOperationStep(row.step),
    trigger:
      row.trigger === "quick_pr_button" ||
      row.trigger === "manual_pr_form" ||
      row.trigger === "system_retry" ||
      row.trigger === "future_yolo"
        ? row.trigger
        : "system_retry",
    actorUserId: normalizeString(row.actorUserId),
    requestId: normalizeString(row.requestId),
    parentOperationId: normalizeString(row.parentOperationId),
    title: normalizeString(row.title),
    summary: normalizeString(row.summary),
    draft: normalizeBoolean(row.draft),
    requestPayload: normalizeOperationPayload(row.requestPayload) as PrFlowOperationRequestPayload | null,
    resultPayload: normalizeOperationPayload(row.resultPayload) as PrFlowOperationResultPayload | null,
    errorCode: normalizeString(row.errorCode),
    errorMessage: normalizeString(row.errorMessage),
    createdAt: normalizeNumber(row.createdAt) ?? 0,
    updatedAt: normalizeNumber(row.updatedAt) ?? 0,
    terminalAt: normalizeNumber(row.terminalAt),
  };
}

function normalizeEntity(value: unknown): PrEntitySnapshot {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {
      prNumber: null,
      prUrl: null,
      prState: "none",
      baseBranch: null,
      headBranch: null,
      mergeable: null,
      mergeableState: null,
      lastSyncedAt: null,
      source: "session_meta",
    };
  }

  const row = value as Record<string, unknown>;
  return {
    prNumber: normalizeNumber(row.prNumber),
    prUrl: normalizeString(row.prUrl),
    prState: normalizePrEntityState(row.prState),
    baseBranch: normalizeString(row.baseBranch),
    headBranch: normalizeString(row.headBranch),
    mergeable: normalizeBoolean(row.mergeable),
    mergeableState: normalizeString(row.mergeableState),
    lastSyncedAt: normalizeNumber(row.lastSyncedAt),
    source:
      row.source === "github_webhook" ||
      row.source === "github_poll" ||
      row.source === "operation_result"
        ? row.source
        : "session_meta",
  };
}

function normalizeRepoReadiness(value: unknown): RepoReadinessSnapshot | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  return {
    source: row.source === "cached_git_status" ? "cached_git_status" : "unknown",
    sandboxStatus: typeof row.sandboxStatus === "string" ? row.sandboxStatus : "none",
    sandboxAvailable: row.sandboxAvailable === true,
    baseBranch: normalizeString(row.baseBranch),
    headBranch: normalizeString(row.headBranch),
    currentBranch: normalizeString(row.currentBranch),
    hasUpstream: row.hasUpstream !== false,
    ahead: normalizeNumber(row.ahead) ?? 0,
    behind: normalizeNumber(row.behind) ?? 0,
    commitsAheadOfBase: normalizeNumber(row.commitsAheadOfBase) ?? 0,
    hasUncommittedChanges: row.hasUncommittedChanges === true,
    hasBranchCommits: row.hasBranchCommits === true,
    hasUnpushedCommits: row.hasUnpushedCommits === true,
    hasQuickPrSourceChanges: row.hasQuickPrSourceChanges === true,
    onBaseBranch: row.onBaseBranch === true,
  };
}

export function normalizeSessionPrFlowSnapshot(value: unknown): SessionPrFlowSnapshot | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const recentOperations = Array.isArray(row.recentOperations)
    ? row.recentOperations.map(normalizeOperation).filter((item): item is PrFlowOperationRecord => item !== null)
    : [];

  return {
    entity: normalizeEntity(row.entity),
    activeOperation: normalizeOperation(row.activeOperation),
    recentOperations,
    repoReadiness: normalizeRepoReadiness(row.repoReadiness),
  };
}

export const useSessionPrFlow = create<SessionPrFlowStore>((set) => ({
  snapshots: {},
  snapshotTimestamps: {},
  setSnapshot: (sessionId, snapshot) => {
    if (!sessionId) return false;

    let applied = false
    set((state) => {
      const currentTimestamp = state.snapshotTimestamps[sessionId] ?? null
      const nextTimestamp = getSessionPrFlowSnapshotTimestamp(snapshot)

      if (
        currentTimestamp !== null &&
        (nextTimestamp === null || nextTimestamp < currentTimestamp)
      ) {
        return state
      }

      applied = true
      return {
        snapshots: {
          ...state.snapshots,
          [sessionId]: snapshot,
        },
        snapshotTimestamps: {
          ...state.snapshotTimestamps,
          [sessionId]: nextTimestamp,
        },
      }
    });

    return applied
  },
  clearSnapshot: (sessionId) => {
    if (!sessionId) return;
    set((state) => {
      if (!(sessionId in state.snapshots) && !(sessionId in state.snapshotTimestamps)) return state;
      const nextSnapshots = { ...state.snapshots };
      const nextTimestamps = { ...state.snapshotTimestamps };
      delete nextSnapshots[sessionId];
      delete nextTimestamps[sessionId];
      return {
        snapshots: nextSnapshots,
        snapshotTimestamps: nextTimestamps,
      };
    });
  },
}));

export function isRenderablePrFlowOperation(
  operation: PrFlowOperationRecord | null | undefined,
): operation is PrFlowOperationRecord {
  return Boolean(operation && PR_FLOW_RENDERABLE_KINDS.has(operation.kind))
}

export function isTerminalSuccessPrFlowOperation(
  operation: PrFlowOperationRecord | null | undefined,
): operation is PrFlowOperationRecord {
  return Boolean(
    isRenderablePrFlowOperation(operation) &&
    PR_FLOW_TERMINAL_SUCCESS_STATUSES.has(operation.status),
  )
}

export function getPrFlowOperationTimestamp(operation: PrFlowOperationRecord): number {
  return operation.terminalAt ?? operation.updatedAt ?? operation.createdAt
}

export function getSessionPrFlowSnapshotTimestamp(
  snapshot: SessionPrFlowSnapshot | null,
): number | null {
  if (!snapshot) return null

  const operationTimestamps = [snapshot.activeOperation, ...snapshot.recentOperations]
    .filter((operation): operation is PrFlowOperationRecord => operation !== null)
    .map(getPrFlowOperationTimestamp)

  if (operationTimestamps.length > 0) {
    return Math.max(...operationTimestamps)
  }

  return snapshot.entity.lastSyncedAt ?? null
}

export function getPrimaryPrFlowOperation(
  snapshot: SessionPrFlowSnapshot | null,
): PrFlowOperationRecord | null {
  if (!snapshot) return null;
  if (snapshot.activeOperation) return snapshot.activeOperation;
  return snapshot.recentOperations.find(isRenderablePrFlowOperation) ?? null;
}

export function getFooterPrFlowOperation(
  snapshot: SessionPrFlowSnapshot | null,
): PrFlowOperationRecord | null {
  if (!snapshot) return null

  if (isRenderablePrFlowOperation(snapshot.activeOperation)) {
    return snapshot.activeOperation
  }

  const latestRecentOperation = snapshot.recentOperations.find(isRenderablePrFlowOperation) ?? null
  if (!latestRecentOperation) return null

  return latestRecentOperation.status === "failed" || latestRecentOperation.status === "cancelled"
    ? latestRecentOperation
    : null
}

export function getLatestQuickPrOperation(
  snapshot: SessionPrFlowSnapshot | null,
): PrFlowOperationRecord | null {
  if (!snapshot) return null

  if (
    snapshot.activeOperation &&
    (snapshot.activeOperation.kind === "quick_pr" || snapshot.activeOperation.kind === "update_pr")
  ) {
    return snapshot.activeOperation
  }

  return snapshot.recentOperations.find(
    (operation) => operation.kind === "quick_pr" || operation.kind === "update_pr",
  ) ?? null
}

export function listTerminalResultPrFlowOperations(
  snapshot: SessionPrFlowSnapshot | null,
): PrFlowOperationRecord[] {
  if (!snapshot) return []

  return snapshot.recentOperations
    .filter(isTerminalSuccessPrFlowOperation)
    .sort((left, right) => getPrFlowOperationTimestamp(left) - getPrFlowOperationTimestamp(right))
}

export type DerivedQuickPrUiState = {
  operationId: string;
  operationKind: "quick_pr" | "update_pr";
  step: "idle" | "queued" | "resuming" | "checking" | "staging" | "generating_pr" | "committing" | "pushing" | "creating_pr" | "done" | "error";
  error: string | null;
  errorCode: string | null;
  behind: number | null;
  result: { prNumber: number; prUrl: string; prState: string; updatedExisting?: boolean } | null;
};

export function deriveQuickPrUiState(
  snapshot: SessionPrFlowSnapshot | null,
): DerivedQuickPrUiState | null {
  const operation = getPrimaryPrFlowOperation(snapshot);
  if (!operation || (operation.kind !== "quick_pr" && operation.kind !== "update_pr")) {
    return null;
  }
  if (operation.status === "cancelled") {
    return null;
  }

  const result = operation.resultPayload && typeof operation.resultPayload.prNumber === "number" && operation.resultPayload.prUrl
    ? {
      prNumber: operation.resultPayload.prNumber,
      prUrl: operation.resultPayload.prUrl,
      prState: operation.resultPayload.prState ?? "open",
      updatedExisting: operation.resultPayload.updatedExisting === true,
    }
    : null;

  let step: DerivedQuickPrUiState["step"] = "checking";
  if (operation.status === "failed") {
    step = "error";
  } else if (operation.status === "succeeded" || operation.status === "reconciled") {
    step = "done";
  } else {
    switch (operation.step) {
      case "queued":
        step = "queued";
        break;
      case "resuming_sandbox":
        step = "resuming";
        break;
      case "staging":
        step = "staging";
        break;
      case "committing":
        step = "committing";
        break;
      case "pushing":
        step = "pushing";
        break;
      case "creating_pr":
      case "linking_existing_pr":
        step = "creating_pr";
        break;
      case "awaiting_draft":
      case "awaiting_finalize":
        step = "generating_pr";
        break;
      default:
        step = "checking";
        break;
    }
  }

  return {
    operationId: operation.operationId,
    operationKind: operation.kind,
    step,
    error: operation.errorMessage,
    errorCode: operation.errorCode,
    behind:
      operation.errorCode === "BRANCH_BEHIND_REMOTE" &&
      snapshot?.repoReadiness &&
      snapshot.repoReadiness.behind > 0
        ? snapshot.repoReadiness.behind
        : null,
    result,
  };
}
